import { type ItemId, isItemId } from '@/data/items';

/**
 * Items lying on the ground.
 *
 * Dropping has to put something *somewhere*, or it is deletion wearing a
 * friendlier word. A dropped stack stays on the tile it was left on, in the
 * area it was left in, and can be picked back up — so a misdropped rare fish
 * is a walk, not a loss.
 *
 * Kept per area rather than globally: what you left on the beach is on the
 * beach, and an area's items can be looked up without filtering the world.
 */

export interface GroundItem {
  readonly x: number;
  readonly y: number;
  readonly id: ItemId;
  count: number;
}

export interface GroundItemSave {
  readonly x: number;
  readonly y: number;
  readonly id: string;
  readonly count: number;
}

export class GroundItems {
  /** Keyed by area id. */
  private readonly byArea = new Map<string, GroundItem[]>();

  /** Everything lying in an area, for rendering and pickup checks. */
  in(areaId: string): readonly GroundItem[] {
    return this.byArea.get(areaId) ?? [];
  }

  /**
   * Leave a stack on a tile.
   *
   * Dropping the same item on the same tile grows the pile rather than
   * stacking two markers on one square, which would leave the second
   * invisible and unrecoverable until the first was taken.
   */
  drop(areaId: string, x: number, y: number, id: ItemId, count: number): void {
    if (count <= 0) return;
    const items = this.byArea.get(areaId) ?? [];

    const existing = items.find((item) => item.x === x && item.y === y && item.id === id);
    if (existing) {
      existing.count += count;
    } else {
      items.push({ x, y, id, count });
    }

    this.byArea.set(areaId, items);
  }

  /** What is lying on a tile, if anything. */
  at(areaId: string, x: number, y: number): GroundItem | null {
    return this.byArea.get(areaId)?.find((item) => item.x === x && item.y === y) ?? null;
  }

  /** Take a pile off the ground. Returns what was there. */
  take(areaId: string, x: number, y: number): GroundItem | null {
    const items = this.byArea.get(areaId);
    if (!items) return null;

    const index = items.findIndex((item) => item.x === x && item.y === y);
    if (index === -1) return null;

    const [taken] = items.splice(index, 1);
    return taken ?? null;
  }

  toSave(): Record<string, GroundItemSave[]> {
    const out: Record<string, GroundItemSave[]> = {};
    for (const [areaId, items] of this.byArea) {
      if (items.length === 0) continue;
      out[areaId] = items.map((item) => ({
        x: item.x,
        y: item.y,
        id: item.id,
        count: item.count,
      }));
    }
    return out;
  }

  static fromSave(value: unknown): GroundItems {
    const ground = new GroundItems();
    if (!value || typeof value !== 'object') return ground;

    for (const [areaId, entries] of Object.entries(value as Record<string, unknown>)) {
      if (!Array.isArray(entries)) continue;
      for (const entry of entries) {
        const item = entry as Partial<GroundItemSave>;
        if (!isItemId(item.id)) continue;
        const count = Math.floor(Number(item.count));
        const x = Math.floor(Number(item.x));
        const y = Math.floor(Number(item.y));
        if (!Number.isFinite(count) || count <= 0) continue;
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        ground.drop(areaId, x, y, item.id, count);
      }
    }

    return ground;
  }
}
